import type { AppState } from '@/store/rootTypes'
import type { StudentAccount, StudentWorkspace } from '@/entities/student-workspace'
import { createInitialGameState } from '@/engine/gameEngine'
import { createEmptyArchiveStats } from '@/store/retention'
import { createLearningStage } from '@/entities/stage'
import { normalizeAITeacherSession } from '@/entities/ai-teacher'

export function createStudentId() {
  return `student-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export function createEmptyWorkspace(account: Pick<StudentAccount, 'name' | 'grade' | 'avatar'>): StudentWorkspace {
  return {
    profile: null,
    student: {
      name: account.name, grade: account.grade, avatar: account.avatar,
      layer: null, style: null, totalStars: 0, streak: 1, completedUnits: [], completedLessons: [],
    },
    currentGrade: account.grade, pretestScore: null, pretestLayer: null,
    learningStyle: null, learnerType: null,
    weeklyPlanActive: false, weeklyPlanStartDate: null, weeklyTaskDone: {}, taskCompletedAt: {},
    currentPlan: null, currentPlanKey: null, formativeTaskSessions: [], attempts: [], currentReport: null, learningEvents: [], archiveStats: createEmptyArchiveStats(),
    completedTasks: {}, stars: 0, streak: 1,
    manualLayerOverride: null, dynamicLayer: null, currentStage: null,
    wrongQuestions: [], assessmentSessions: [], assessmentRecords: [], rewardClaims: [], vocabMastery: {},
    initialProfileSessions: [],
    game: createInitialGameState(), lastLayerChangeReason: null,
    currentOutfit: 'none', unlockedOutfits: [], ownedOutfits: [],
    aiTeacherSession: null,
  }
}


export function normalizeWorkspace(account: StudentAccount, workspace: Partial<StudentWorkspace> | undefined): StudentWorkspace {
  const base = createEmptyWorkspace(account)
  const attempts = workspace?.attempts ?? []
  const inferredStartDate = [...attempts]
    .filter((attempt) => Number.isFinite(new Date(attempt.completedAt).getTime()))
    .sort((left, right) => new Date(left.completedAt).getTime() - new Date(right.completedAt).getTime())[0]?.completedAt ?? null
  const weeklyPlanStartDate = workspace?.weeklyPlanStartDate ?? inferredStartDate
  const normalized = {
    ...base,
    ...workspace,
    student: { ...base.student, ...(workspace?.student ?? {}) },
    weeklyPlanActive: workspace?.weeklyPlanActive ?? Boolean(weeklyPlanStartDate),
    weeklyPlanStartDate,
    weeklyTaskDone: workspace?.weeklyTaskDone ?? {},
    taskCompletedAt: workspace?.taskCompletedAt ?? {},
    formativeTaskSessions: workspace?.formativeTaskSessions ?? [],
    attempts,
    learningEvents: workspace?.learningEvents ?? [],
    archiveStats: { ...createEmptyArchiveStats(), ...(workspace?.archiveStats ?? {}) },
    completedTasks: workspace?.completedTasks ?? {},
    wrongQuestions: workspace?.wrongQuestions ?? [],
    assessmentSessions: workspace?.assessmentSessions ?? [],
    assessmentRecords: workspace?.assessmentRecords ?? [],
    rewardClaims: workspace?.rewardClaims ?? [],
    initialProfileSessions: workspace?.initialProfileSessions ?? [],
    vocabMastery: workspace?.vocabMastery ?? {},
    game: workspace?.game ?? createInitialGameState(),
    currentOutfit: workspace?.currentOutfit ?? 'none',
    unlockedOutfits: workspace?.unlockedOutfits ?? [],
    ownedOutfits: workspace?.ownedOutfits ?? [],
    aiTeacherSession: workspace?.aiTeacherSession
      ? normalizeAITeacherSession(workspace.aiTeacherSession)
      : null,
  }
  if (!normalized.currentStage) {
    const baselineLayer = normalized.profile?.recommendedLevel ?? normalized.pretestLayer ?? normalized.student.layer
    if (baselineLayer) normalized.currentStage = createLearningStage({ layer: baselineLayer, source: 'pretest' })
  }
  return normalized
}

export function workspaceFromActiveState(state: AppState): StudentWorkspace {
  return {
    profile: state.profile, student: state.student, currentGrade: state.currentGrade,
    pretestScore: state.pretestScore, pretestLayer: state.pretestLayer,
    learningStyle: state.learningStyle, learnerType: state.learnerType,
    weeklyPlanActive: state.weeklyPlanActive, weeklyPlanStartDate: state.weeklyPlanStartDate, weeklyTaskDone: state.weeklyTaskDone, taskCompletedAt: state.taskCompletedAt,
    currentPlan: state.currentPlan, currentPlanKey: state.currentPlanKey, formativeTaskSessions: state.formativeTaskSessions, attempts: state.attempts, currentReport: state.currentReport, learningEvents: state.learningEvents, archiveStats: state.archiveStats,
    completedTasks: state.completedTasks, stars: state.stars, streak: state.streak,
    manualLayerOverride: state.manualLayerOverride, dynamicLayer: state.dynamicLayer, currentStage: state.currentStage,
    wrongQuestions: state.wrongQuestions, assessmentSessions: state.assessmentSessions, assessmentRecords: state.assessmentRecords, rewardClaims: state.rewardClaims, vocabMastery: state.vocabMastery,
    initialProfileSessions: state.initialProfileSessions,
    game: state.game, lastLayerChangeReason: state.lastLayerChangeReason,
    currentOutfit: state.currentOutfit, unlockedOutfits: state.unlockedOutfits, ownedOutfits: state.ownedOutfits,
    aiTeacherSession: state.aiTeacherSession,
  }
}

export function activeFieldsFromWorkspace(workspace: StudentWorkspace) {
  return {
    ...workspace,
    aiTeacherResult: workspace.aiTeacherSession?.result ?? null,
    aiTeacherFingerprint: workspace.aiTeacherSession?.wrongItemFingerprint ?? '',
    sessionStats: null, lastXpEvent: null, pendingAchievements: [],
  }
}

export function activeWorkspacePatch(
  state: AppState,
  patch: Partial<StudentWorkspace>,
): Partial<Pick<AppState, 'workspaces'>> {
  if (!state.activeStudentId) return {}
  const workspace = state.workspaces[state.activeStudentId]
  if (!workspace) return {}
  return {
    workspaces: {
      ...state.workspaces,
      [state.activeStudentId]: { ...workspace, ...patch },
    },
  }
}
